import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Paper,
    Alert
} from '@mui/material';
import {
    ArrowBack as BackIcon,
    Save as SaveIcon
} from '@mui/icons-material';
import { useDataProvider, useNotify, Title } from 'react-admin';
import { useNavigate, useParams } from 'react-router-dom';
import VideoLessonEditor from './editors/VideoLessonEditor';
import DocumentLessonEditor from './editors/DocumentLessonEditor';
import InfoTextEditor from './editors/InfoTextEditor';
import MultipleChoiceEditor from './editors/MultipleChoiceEditor';
import FillInBlankEditor from './editors/FillInBlankEditor';
import FreeTextEditor from './editors/FreeTextEditor';
import ScormEditor from './editors/ScormEditor';
import H5PEditor from './editors/H5PEditor';
import XAPIEditor from './editors/XAPIEditor';
import DragDropEditor from './editors/DragDropEditor';
import MatchingEditor from './editors/MatchingEditor';
import SortingEditor from './editors/SortingEditor';
import HotspotEditor from './editors/HotspotEditor';
import TimelineEditor from './editors/TimelineEditor';
import { BlockLessonEditor } from './editors/BlockEditor/BlockLessonEditor';

interface Lesson {
    id: number;
    title: string;
    type: string;
    description?: string;
    content?: any;
    course: any;
}

const LessonContentEditor: React.FC = () => {
    const { lessonId } = useParams<{ lessonId: string }>();
    const [lesson, setLesson] = useState<Lesson | null>(null);
    const [content, setContent] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const dataProvider = useDataProvider();
    const notify = useNotify();
    const navigate = useNavigate();

    useEffect(() => {
        if (lessonId) {
            loadLesson();
        }
    }, [lessonId]);

    const loadLesson = async () => {
        setLoading(true);
        try {
            const { data } = await dataProvider.getOne('lessons', { id: parseInt(lessonId!) });
            setLesson(data as Lesson);
            setContent(data.content || {});
        } catch (error) {
            notify('Fehler beim Laden der Lektion', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const validateContent = () => {
        if (!lesson) return true;

        // Validate Drag & Drop content
        if (lesson.type === 'drag_drop') {
            const zones = content?.zones || [];
            const items = content?.items || [];

            if (zones.length === 0) {
                notify('Bitte fügen Sie mindestens eine Drop-Zone hinzu', { type: 'warning' });
                return false;
            }

            if (items.length === 0) {
                notify('Bitte fügen Sie mindestens ein ziehbares Element hinzu', { type: 'warning' });
                return false;
            }

            // Check if there are enough zones
            const uniqueZones = new Set(items.map((item: any) => item.correctZone));
            if (uniqueZones.size > zones.length) {
                notify('Fehler: Es gibt mehr verschiedene Ziel-Zonen als definierte Drop-Zonen', { type: 'error' });
                return false;
            }

            // Check if all items have valid correctZone
            const zoneIds = new Set(zones.map((zone: any) => zone.id));
            const invalidItems = items.filter((item: any) => !zoneIds.has(item.correctZone));
            if (invalidItems.length > 0) {
                notify('Fehler: Einige Elemente verweisen auf nicht existierende Drop-Zonen', { type: 'error' });
                return false;
            }
        }

        return true;
    };

    const handleSave = async () => {
        if (!lesson) return;

        // Validate content before saving
        if (!validateContent()) {
            return;
        }

        setSaving(true);
        try {
            await dataProvider.update('lessons', {
                id: lesson.id,
                data: { content },
                previousData: lesson
            });
            notify('Inhalt erfolgreich gespeichert', { type: 'success' });
        } catch (error) {
            notify('Fehler beim Speichern des Inhalts', { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const renderEditor = () => {
        if (!lesson) return null;

        switch (lesson.type) {
            case 'video':
                return <VideoLessonEditor content={content} onChange={setContent} />;
            case 'document':
                return <DocumentLessonEditor content={content} onChange={setContent} />;
            case 'info_text':
                return <InfoTextEditor content={content} onChange={setContent} />;
            case 'multiple_choice':
                return <MultipleChoiceEditor content={content} onChange={setContent} />;
            case 'fill_in_blank':
                return <FillInBlankEditor content={content} onChange={setContent} />;
            case 'free_text':
                return <FreeTextEditor content={content} onChange={setContent} />;
            case 'scorm':
                return <ScormEditor content={content} onChange={setContent} />;
            case 'h5p':
                return <H5PEditor content={content} onChange={setContent} />;
            case 'xapi':
                return <XAPIEditor content={content} onChange={setContent} />;
            case 'drag_drop':
                return <DragDropEditor content={content} onChange={setContent} />;
            case 'matching':
                return <MatchingEditor content={content} onChange={setContent} />;
            case 'sorting':
                return <SortingEditor content={content} onChange={setContent} />;
            case 'hotspot':
                return <HotspotEditor content={content} onChange={setContent} />;
            case 'timeline':
                return <TimelineEditor content={content} onChange={setContent} />;
            case 'blocks':
                // Autorentool v2: eigener Host mit Autosave + Versionen, speichert selbst.
                return <BlockLessonEditor lesson={{ id: lesson.id, title: lesson.title, type: lesson.type, minScore: (lesson as { minScore?: number | null }).minScore ?? null, content: lesson.content }} />;
            default:
                return (
                    <Alert severity="warning">
                        Unbekannter Lektionstyp: {lesson.type}
                    </Alert>
                );
        }
    };

    if (loading) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography>Lade Lektion...</Typography>
            </Box>
        );
    }

    if (!lesson) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography>Lektion nicht gefunden</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Title title={`Inhalt bearbeiten - ${lesson.title}`} />

            <Box sx={{ mb: 3 }}>
                <Button startIcon={<BackIcon />} onClick={() => navigate(-1)}>
                    Zurück zu Lektionen
                </Button>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    <Typography variant="h4">{lesson.title}</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Typ: {lesson.type}
                    </Typography>
                </Box>
                {lesson.type !== 'blocks' && (
                    <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? 'Speichert...' : 'Speichern'}
                    </Button>
                )}
            </Box>

            {lesson.description && (
                <Alert severity="info" sx={{ mb: 3 }}>
                    {lesson.description}
                </Alert>
            )}

            <Paper sx={{ p: 3 }}>
                {renderEditor()}
            </Paper>
        </Box>
    );
};

export default LessonContentEditor;
